import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import useTaskStats from '@/features/task/hooks/useTaskStats';
import StatCard from './StatCard';
import { useAuth } from '@/auth/useAuth';
import { ListChecks } from 'lucide-react';
import useTaskStates from '@/features/taskState/hooks/useTaskStates';
import { IconRenderer, isIconValue } from '@/features/shared/components/IconPicker';
import { isRestrictedToOwnTasks } from '@/features/task/utils/filterUtils';
const DEFAULT_ICON = _jsx(ListChecks, { className: "w-6 h-6" });
const buildIcon = (iconSvg) => {
    if (!iconSvg) {
        return DEFAULT_ICON;
    }
    if (isIconValue(iconSvg)) {
        return _jsx(IconRenderer, { value: iconSvg, size: 24, className: "w-6 h-6" });
    }
    return (_jsx("span", { className: "w-6 h-6 flex items-center justify-center", dangerouslySetInnerHTML: { __html: iconSvg } }));
};
const TaskStats = ({ selectedStatus, onStatusFilterChange }) => {
    const { taskStates, loading: statesLoading } = useTaskStates();
    const { user, loading: authLoading } = useAuth();
    const shouldRestrictStats = isRestrictedToOwnTasks(user);
    const userId = shouldRestrictStats ? user.id : null;
    const statsFilters = useMemo(() => {
        if (!shouldRestrictStats || !userId) {
            return undefined;
        }
        return { assignees: [userId] };
    }, [shouldRestrictStats, userId]);
    const { stats, total, loading } = useTaskStats(statsFilters, !authLoading);
    const statusConfig = useMemo(() => {
        const config = {
            total: { label: 'Total', icon: DEFAULT_ICON, color: '#2563eb' },
        };
        taskStates.forEach(state => {
            config[state.code] = {
                label: state.label,
                icon: buildIcon(state.icon_svg),
                color: state.color ?? null,
            };
        });
        return config;
    }, [taskStates]);
    const orderedStatuses = useMemo(() => {
        const sortedStates = [...taskStates]
            .filter(state => state.indic_display)
            .sort((a, b) => {
            if (a.display_sequence !== b.display_sequence) {
                return a.display_sequence - b.display_sequence;
            }
            if (a.process_sequence !== null && b.process_sequence !== null) {
                return a.process_sequence - b.process_sequence;
            }
            if (a.process_sequence !== null) {
                return -1;
            }
            if (b.process_sequence !== null) {
                return 1;
            }
            return a.label.localeCompare(b.label);
        })
            .map(state => state.code);
        return shouldRestrictStats ? sortedStates : ['total', ...sortedStates];
    }, [taskStates, shouldRestrictStats]);
    if (loading || statesLoading || authLoading) {
        return _jsx("p", { children: "Chargement..." });
    }
    const displayedStats = orderedStatuses.map(status => {
        if (status === 'total') {
            return { status, count: total };
        }
        const found = stats.find(s => s.status === status);
        return { status, count: found ? found.count : 0 };
    });
    const normalizedStatus = selectedStatus ?? '';
    return (_jsx("div", { className: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4", children: displayedStats.map(stat => {
            const config = statusConfig[stat.status] || { label: stat.status, icon: null, color: 'gray' };
            const handleClick = () => {
                if (!onStatusFilterChange)
                    return;
                if (stat.status === 'total') {
                    onStatusFilterChange('');
                    return;
                }
                const nextStatus = normalizedStatus === stat.status ? '' : stat.status;
                onStatusFilterChange(nextStatus);
            };
            return (_jsx(StatCard, { icon: config.icon, label: config.label, value: stat.count, color: config.color, onClick: onStatusFilterChange ? handleClick : undefined, isActive: stat.status !== 'total' ? normalizedStatus === stat.status : normalizedStatus === '' }, stat.status));
        }) }));
};
export default TaskStats;
